import { FormProvider, getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import {
	Badge,
	Box,
	Flex,
	Heading,
	Popover,
	Separator,
	Text,
	Tooltip,
	VisuallyHidden,
} from '@radix-ui/themes'
import {
	useTransition as useTransitionSpring,
	config as springConfig,
} from '@react-spring/web'
import { Link, NavLink, useFetcher, useLocation, useSearchParams } from '@remix-run/react'
import cn from 'clsx'
import parse from 'html-react-parser'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { RiSearch2Line } from 'react-icons/ri'

import { z } from 'zod'
import { FormDefaults } from '~/components/common/form-defaults.tsx'
import { Image } from '~/components/ui/image'
import { type GetProducts } from '~/models/ecommerce-providers/sylius/shop/fetch/product/get-products'
import { type Locale } from '~/models/locale'
import {
	type App,
	type FormEnum,
	appView,
	ROOT_I18NEXT,
	ROOT_INPUTS,
} from '~/routes/_layout/root.component'
import { schemaDefault } from '~/utils/form-defaults.ts'
import { hasHeader } from '~/routes/_layout/navbar.tsx'
import { TextField } from '~/components/ui/text-field.tsx'

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
	subaction = 'subaction',
	q = 'q',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	q = 'q',
}

/* -------------------------------------------------------------------------------------------------
 * Form
 * -----------------------------------------------------------------------------------------------*/

const FORM: FormEnum = {
	id: 'search-form',
	action: '/actions/search',
	fetcherKey: 'search',
	subaction: 'search',
	redirect: '/products',
} as const

const schema = z.object({
	...schemaDefault,
})

/* -------------------------------------------------------------------------------------------------
 * Search
 * -----------------------------------------------------------------------------------------------*/

type SearchProps = {
  locale: Locale
  path: string
  className: string
}

const Search: React.FC<SearchProps> = ({ locale, path, className }) => {
	const searchFetcher = useFetcher({ key: FORM.subaction })
  const formValues = {
    [ROOT_INPUTS.subaction]: FORM.subaction,
    [ROOT_INPUTS.noJs]: 'false',
  }
	const [form, fields] = useForm({
		defaultValue: {
			...formValues,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: schema })
		},
		shouldValidate: 'onSubmit',
	})
  const { pathname } = useLocation()

	const { t } = useTranslation(ROOT_I18NEXT.routeRoot)
	const [open, setOpen] = React.useState(false)
	const inputRef = React.useRef<HTMLInputElement | null>(null)
	const [searchParams, setSearchParams] = useSearchParams()
	const [container, setContainer] = React.useState<HTMLElement | null>(null)
	const transitions = useTransitionSpring(open, {
		from: { opacity: 0, y: -10 },
		enter: { opacity: 1, y: 0 },
		leave: { opacity: 0, y: 10 },
		config: springConfig.slow,
	})

	const [query, setQuery] = React.useState(
		searchParams.get(SEARCH_PARAMS.q) || '',
	)

	React.useEffect(() => {
		setQuery(searchParams.get(SEARCH_PARAMS.q) || '')
	}, [setQuery, searchParams])

	React.useEffect(() => {
		setSearchParams((prev) => {
			if (query !== '') {
				prev.set(SEARCH_PARAMS.q, query)
			} else {
				prev.delete(SEARCH_PARAMS.q)
			}
			return prev
		})
	}, [query, setSearchParams])

	return (
		<FormProvider context={form.context}>
			<Flex
				asChild
				align="center"
				position="relative"
				p="2"
			>
				<searchFetcher.Form
					method="get"
					action={FORM.action}
					{...getFormProps(form)}
				>
					<FormDefaults
            form={form}
            fields={fields}
            values={formValues}
          />
					<Popover.Root open={open} onOpenChange={setOpen}>
						<Popover.Trigger>
              <NavLink
                end
                prefetch="intent"
                to={path}
                className={cn('flex flex-col gap-y-0.5 items-center text-accent-9',
                  'font-bold whitespace-nowrap',
                  'transition duration-300 ease-in-out',
                  'hover:text-accent-8', className)}
                onClick={(e) => {
                  e.preventDefault()
                  setOpen(true)
                }}
              >
                <RiSearch2Line size="1.6em" />
                <Text size="1" weight="light">
                  {t(`label.search`)}
                </Text>
              </NavLink>
						</Popover.Trigger>
            <Popover.Content
              container={container}
              onOpenAutoFocus={(e) => {
                e.preventDefault()
                inputRef.current?.focus()
              }}
              side="bottom"
            >
              <Flex direction="column" mt="3">
                <TextField
                  ref={inputRef}
                  meta={fields[INPUTS.q]}
                  t={t}
                  onChange={(e) => {
                    if (e.currentTarget.value.length) {
                      searchFetcher.submit(e.currentTarget.form)
                      setOpen(true)
                    }
                    setQuery(e.currentTarget.value.replace(/[^a-zA-Z ]/g, ''))
                  }}
                >
                  {t('label.search')}
                </TextField>
                {transitions((styles, item) =>
                  item ? (
                    // @ts-ignore
                    inputRef.current?.value.length &&
                    searchFetcher.data?.products ? (
                      // @ts-ignore
                      searchFetcher.data.products.items?.length ? (
                        <Flex asChild direction="column" mt="3" gapY="3">
                          <ul>
                            {
                              // @ts-ignore
                              (
                                searchFetcher.data.products as GetProducts
                              ).items!.map(({ name, slug, images }) => (
                                <li key={slug}>
                                  <Flex asChild align="center" gapX="3" p="2">
                                    <Link
                                      prefetch="viewport"
                                      to={`/${locale}/products/${slug}`}
                                      className="rounded transition duration-300 ease-in-out hover:shadow"
                                      onClick={() => setOpen(false)}
                                    >
                                      <Box width="3rem">
                                        <Image
                                          alt={name!}
                                          src={images![0].path!}
                                        />
                                      </Box>
                                      <Heading
                                        as="h4"
                                        size="2"
                                        weight="medium"
                                        color="gray"
                                        className="!break-keep"
                                      >
                                        {query === '' ? (
                                          <>{name}</>
                                        ) : (
                                          <>
                                            {parse(
                                              name!.replace(
                                                new RegExp(query, 'gi'),
                                                (match) =>
                                                  `<mark style="background-color: var(--green-5); border-radius: 0.375rem; padding: 0.125rem;">${match}</mark>`,
                                              ),
                                            )}
                                          </>
                                        )}
                                      </Heading>
                                    </Link>
                                  </Flex>
                                </li>
                              ))
                            }
                          </ul>
                        </Flex>
                      ) : (
                        <Text
                          align="center"
                          mt="2"
                          color="gray"
                          weight="medium"
                          size="2"
                        >
                          {t('common.noItemsFound')}
                        </Text>
                      )
                    ) : (
                      <Text
                        mt="1"
                        color="gray"
                        weight="light"
                        size="1"
                      >
                        {t('common.typeToStartSearch')}
                      </Text>
                    )
                  ) : null,
                )}
              </Flex>
            </Popover.Content>
          </Popover.Root>
          <Box ref={setContainer}/>
        </searchFetcher.Form>
      </Flex>
    </FormProvider>
  )
}
Search.displayName = 'Search'

/* -----------------------------------------------------------------------------------------------*/

export {
  Search,
  SEARCH_PARAMS as SEARCH_SEARCH_PARAMS,
  INPUTS as SEARCH_INPUTS,
  FORM as SEARCH_FORM,
  schema as searchSchema,
}
